import type { QueryClient } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	HeadContent,
	Outlet,
	redirect,
	Scripts,
	useRouterState,
} from "@tanstack/react-router";
import type { TolgeeInstance } from "@tolgee/web";
import { type createStore, useAtomValue } from "jotai";
import type { ReactNode } from "react";
import ComponentInitializer from "#/components/initializer/ComponentInitializer.tsx";
import { localeFromPath, stripLocalePrefix } from "#/lib/locale";
import { routeHeadFromContext } from "#/lib/route-head";
import { DEFAULT_DESCRIPTION, isLearnArticlePath } from "#/lib/seo";
import { THEME_INIT_SCRIPT } from "#/lib/theme";
import { canReloadNow, isVersionStale } from "#/lib/version-check";
import { localeAtom } from "#/stores/locale-store";
import { AttributionSync } from "../components/analytics/attribution-sync";
import { AuthFunnelSync } from "../components/analytics/auth-funnel-sync";
import { PageViewSync } from "../components/analytics/pageview-sync";
import { AuthSync } from "../components/auth/auth-sync";
import { LoginDialog } from "../components/auth/login-dialog";
import Footer from "../components/Footer";
import Header from "../components/header";
import { HyperUserEventsSync } from "../components/hyperliquid/hyper-user-events-sync";
import { LocaleSync } from "../components/locale/locale-sync";
import { NotFound } from "../components/not-found";
import { ThemeSync } from "../components/theme/theme-sync.tsx";
import { TransferStatusSync } from "../components/wallet/transfer-status-sync";
import appCss from "../styles.css?url";

interface MyRouterContext {
	queryClient: QueryClient;
	/** 요청별 인스턴스(서버) / 단일 인스턴스(클라이언트) — getRouter 참조 */
	tolgee: TolgeeInstance;
	/** Jotai 스토어 — 서버는 요청별, 클라이언트는 기본 스토어 */
	store: ReturnType<typeof createStore>;
}

// 라우트와 무관한 불변 문서 메타 — 라우트별 SEO 태그(buildRouteHead)와 합쳐 head()가 방출한다.
const STATIC_HEAD = {
	meta: [
		{ charSet: "utf-8" },
		{ name: "viewport", content: "width=device-width, initial-scale=1" },
		{
			name: "google-site-verification",
			content: "qT3Msuiqrrij5p8UnNKcMsDLnFs54KYAzVNTKMVlGp4",
		},
		{ name: "theme-color", content: "#000000" },
	],
	links: [
		{ rel: "icon", href: "/favicon.ico", sizes: "any" },
		{
			rel: "icon",
			type: "image/png",
			sizes: "32x32",
			href: "/favicon-32x32.png",
		},
		{
			rel: "icon",
			type: "image/png",
			sizes: "16x16",
			href: "/favicon-16x16.png",
		},
		{
			rel: "apple-touch-icon",
			sizes: "180x180",
			href: "/apple-touch-icon.png",
		},
		{ rel: "manifest", href: "/manifest.json" },
		{
			rel: "preconnect",
			href: "https://app.hyperliquid.xyz",
			crossOrigin: "",
		},
		{ rel: "dns-prefetch", href: "https://api.hyperliquid.xyz" },
		{ rel: "stylesheet", href: appCss },
	],
} as const;

export const Route = createRootRouteWithContext<MyRouterContext>()({
	beforeLoad: async ({ location, preload, context }) => {
		// 배포 버전 스큐 게이트 — 새 배포가 감지된 탭은 다음 내비게이션을 전체 페이지
		// 로드로 전환해 옛 번들에서 벗어난다(스테일 청크 요청 자체가 사라짐). 서버에선 no-op.
		if (!preload && isVersionStale() && canReloadNow()) {
			throw redirect({ href: location.href, reloadDocument: true });
		}
		// 로케일: URL 프리픽스가 권위(/ko/…). 없으면 현재 값 — 서버는 "en", 클라이언트는
		// LocaleSync가 마운트 후 적용한 선호(zh 등). 서버/클라이언트가 같은 규칙으로 판정해
		// 첫 렌더가 일치한다. preload(hover)에서는 언어를 바꾸지 않는다.
		const locale =
			localeFromPath(location.pathname) ?? context.store.get(localeAtom);
		if (!preload) {
			context.store.set(localeAtom, locale);
			await context.tolgee.changeLanguage(locale);
		}
		return { locale };
	},
	// 서버 HTML과 클라이언트 내비게이션 양쪽에서 실행 — HeadContent가 결과를 렌더한다.
	// 자식 라우트가 ssr:false여도 루트 head()는 서버에서 실행되므로 트레이드 페이지의
	// 메타/JSON-LD도 여기서 나온다. learn 글 경로만 자식 head가 소유(fetch한 post 필요).
	head: ({ matches, match }) => {
		const leaf = matches[matches.length - 1] ?? match;
		// 루트만 매치 = 진짜 404 (알 수 없는 경로) → noindex, canonical 생략
		const notFound = matches.length === 1;
		const pathname = notFound ? "/" : leaf.pathname;
		// learn 글 경로는 자식 라우트 head가 소유(fetch한 post 필요) — 여기선 정적 메타만
		const route =
			!notFound && isLearnArticlePath(stripLocalePrefix(pathname))
				? { meta: [], links: [] }
				: routeHeadFromContext(match.context, pathname, { notFound });
		return {
			meta: [...STATIC_HEAD.meta, ...route.meta],
			links: [...STATIC_HEAD.links, ...route.links],
		};
	},
	shellComponent: RootDocument,
	component: RootLayout,
	notFoundComponent: NotFound,
});

// GA4 부트스트랩 — 페이지뷰는 PageViewSync가 쏘므로 send_page_view:false.
const GTAG_INIT_SCRIPT = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-QH0E90GYD7',{send_page_view:false});`;

// Organization + WebSite @graph — 라우트별 JSON-LD(#organization 참조)의 앵커.
const ORG_JSON_LD = JSON.stringify({
	"@context": "https://schema.org",
	"@graph": [
		{
			"@type": "Organization",
			"@id": "https://tenbagger.xyz/#organization",
			name: "Tenbagger",
			alternateName: "텐배거",
			url: "https://tenbagger.xyz",
			logo: {
				"@type": "ImageObject",
				url: "https://tenbagger.xyz/images/tenbagger_preview.jpg",
				width: 1200,
				height: 630,
			},
			sameAs: ["https://x.com/tenbagger_xyz"],
		},
		{
			"@type": "WebSite",
			"@id": "https://tenbagger.xyz/#website",
			url: "https://tenbagger.xyz",
			name: "Tenbagger",
			alternateName: "텐배거",
			description: DEFAULT_DESCRIPTION,
			publisher: { "@id": "https://tenbagger.xyz/#organization" },
			inLanguage: ["en", "ko", "zh"],
		},
	],
});

function RootDocument({ children }: { children: ReactNode }) {
	// 서버: beforeLoad가 요청 경로로 설정한 값 / 클라이언트: LocaleSync가 선호를 얹으면 갱신
	const locale = useAtomValue(localeAtom);
	return (
		// suppressHydrationWarning: 테마 IIFE가 하이드레이션 전에 <html>의 class를 바꾼다(의도된 mismatch).
		<html lang={locale} suppressHydrationWarning>
			<head>
				<HeadContent />
				<script
					async
					src="https://www.googletagmanager.com/gtag/js?id=G-QH0E90GYD7"
				/>
				{/* biome-ignore lint/security/noDangerouslySetInnerHtml: 정적 부트 스크립트 — 구 index.html 인라인 이전 */}
				<script dangerouslySetInnerHTML={{ __html: GTAG_INIT_SCRIPT }} />
				<script
					type="application/ld+json"
					// biome-ignore lint/security/noDangerouslySetInnerHtml: 정적 JSON-LD
					dangerouslySetInnerHTML={{ __html: ORG_JSON_LD }}
				/>
				{/* biome-ignore lint/security/noDangerouslySetInnerHtml: 정적 부트 스크립트 */}
				<script dangerouslySetInnerHTML={{ __html: THEME_INIT_SCRIPT }} />
			</head>
			<body className="font-sans antialiased wrap-anywhere selection:bg-[color-mix(in_srgb,var(--color-theme-primary-normal)_24%,transparent)]">
				<div id="root">{children}</div>
				<Scripts />
			</body>
		</html>
	);
}

function RootLayout() {
	// 랜딩(`/`, `/ko`)은 자체 nav/footer를 가진다 — 앱 크롬(Header/Footer)만 건너뛰고,
	// 인증·WS·전송 상태 sync와 로그인 다이얼로그는 모든 라우트에서 마운트한다.
	// (과거 `_app` pathless 레이아웃은 Privy 번들을 랜딩에서 떼어내기 위한 것 — Privy 제거로 폐기.)
	const isLanding = useRouterState({
		select: (s) =>
			stripLocalePrefix(s.location.pathname.replace(/\/+$/, "") || "/") === "/",
	});
	return (
		<>
			<ThemeSync />
			<LocaleSync />
			<AttributionSync />
			<PageViewSync />
			<AuthSync />
			<AuthFunnelSync />
			<HyperUserEventsSync />
			<TransferStatusSync />
			{isLanding ? (
				<Outlet />
			) : (
				<>
					<Header />
					<Outlet />
					<Footer />
				</>
			)}
			<LoginDialog />
			<ComponentInitializer />
		</>
	);
}
